import { MultiContextData, MultiContextDataItem, ReportContext } from "@reporting/rp-types/analytics";

/**
 * Dashboard item with a runtime-only stable identity. The uid is assigned
 * when a multi context enters the client (or when an item is created),
 * it is stripped before persisting and it never reaches the server
 * (a persisted uid would change the server-side context hash and break
 * copy deduplication). See REFACTORING-context-state.md, section 2.3.
 */
export type UidMultiContextDataItem = MultiContextDataItem & { uid?: string };

/** uid format: 'u' + hex, distinguishable from numeric id/index and hex-encoded param segments of component ids */
export const createItemUid = () => 'u' + crypto.randomUUID().replace(/-/g, '').slice(0, 12);

export const isItemUid = (value: string) => /^u[0-9a-f]{12}$/.test(value ?? '');

/** assign uids to items that are missing one; idempotent, mutates the given context data */
export const ensureItemUids = (contextData: MultiContextData) => {
	contextData?.items?.forEach((item: UidMultiContextDataItem) => item.uid ??= createItemUid());
	return contextData;
}

/** remove runtime uids before the context is persisted; mutates the given context */
export const stripItemUids = <T extends ReportContext>(context: T): T => {
	((context?.context as MultiContextData)?.items ?? []).forEach((item: UidMultiContextDataItem) => delete item.uid);
	return context;
}

/** carry runtime uids over to the saved context returned by the server (items are index-aligned) */
export const copyItemUids = (source: ReportContext, target: ReportContext) => {
	const sourceItems = (source?.context as MultiContextData)?.items as UidMultiContextDataItem[];
	const targetItems = (target?.context as MultiContextData)?.items as UidMultiContextDataItem[];
	if (!sourceItems?.length || !targetItems?.length) return;
	targetItems.forEach((item, i) => item.uid ??= sourceItems[i]?.uid);
}

export const findItemIndexByUid = (contextData: MultiContextData, uid: string) =>
	(contextData?.items ?? []).findIndex((item: UidMultiContextDataItem) => item.uid === uid);

/** component id of a dashboard item, stable across item reordering (uid instead of index) */
export const createDashboardItemComponentId = (idContext: number, item: UidMultiContextDataItem) =>
	`analytics-dashboardItem-${idContext}-${item?.uid}`;
